import Grid from "@mui/material/Grid";
import { DocumentReferenceResponse, GetProjectResponse, useGetApiConfigQuery } from "api/generatedApi";
import { useDeleteApiProjectsByProjectIdDocumentsAndDocumentIdMutation } from "api/generatedApi";
import { CardWithHeaderAndFooter } from "components/shared/card/card-header-footer";
import { useTranslation } from "react-i18next";
import { ProjectDocumentationContent } from "./project-documentation-content";
import { useDocumentationModal } from "./hooks/use-documentation-modal";
import { useEffect, useState } from "react";
import { makeFullPath } from "api/path";
import { useToast } from "shared/toast/hooks/use-toast";
import { UploadDocument } from "shared/editors/hooks/upload-document";
import { useProjectRestrictions } from "shared/user-restrictions/use-project-restrictions";

interface Props {
  project: GetProjectResponse;
}

export const ProjectDocumentation = ({ project }: Props) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<DocumentReferenceResponse[]>([]);

  const [deleteDocument] = useDeleteApiProjectsByProjectIdDocumentsAndDocumentIdMutation();
  const { data: configData } = useGetApiConfigQuery();

  const { canEditDescription } = useProjectRestrictions(project);
  const path = makeFullPath(`/api/projects/${project.id}/documents`);
  const toast = useToast();
  const clickUploadDocument = UploadDocument(path, configData?.maxUploadFileSizeMB);

  useEffect(() => {
    setDocuments(project.documents ?? []);
  }, [project.documents]);

  //upload file
  const handleFileChange = async (file: File) => {
    await clickUploadDocument(file)
      .then((filename) => {
        if (!filename || filename === "") throw new Error();
        toast.success(t("content.overview.documents.documentUploaded", { filename }));
      })
      .catch((error: unknown) => {
        if (error instanceof RangeError) {
          toast.error(t("content.overview.documents.uploadFileSizeError", { maxFileSize: configData?.maxUploadFileSizeMB ?? 10 }));
        } else {
          toast.error(t("content.overview.documents.uploadError"));
        }
      })
      .finally(() => {
        setDocuments(project.documents ?? []);
      });
  };

  const clickDocumentDelete = async (document: DocumentReferenceResponse) => {
    try {
      await deleteDocument({
        projectId: project.id ? project.id : "",
        documentId: document.documentId ? document.documentId : "",
      }).unwrap();
      const res = documents.filter((doc) => doc.documentId !== document.documentId);
      setDocuments(res);
      toast.success(t("content.overview.documents.documentDeleted", { filename: document.name }));
    } catch (e) {
      toast.error(t("content.overview.documents.deleteError", { documentName: document.name }));
    }
  };

  const editProjectDocumentation = useDocumentationModal({ project, documents, handleFileChange, clickDocumentDelete });
  return (
    <Grid item xs={12} lg={4} xl={4} display={"flex"} justifyContent={"center"}>
      <CardWithHeaderAndFooter
        titleNamespace={t("dashboard.projectInfo.documentation.title")}
        description={""}
        height={"420px"}
        headerActionIcon={undefined}
        showHeaderAction={canEditDescription()}
        showBottomAction={"none"}
        headerActionClickedProps={editProjectDocumentation}
        bottomActionClickedProps={() => {}}
        showContent={true}
        noContentText={""}
        showDescription={false}
        hasChildPadding={false}
        titleLeftPadding="30px"
        titleRightPadding="30px"
      >
        <ProjectDocumentationContent project={project} />
      </CardWithHeaderAndFooter>
    </Grid>
  );
};
